import React, { useEffect, useState, createContext, useContext } from 'react';
type UserRole = 'employee' | 'manager' | 'admin';
interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => void;
  switchRole: (role: UserRole) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Check if user is logged in on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // In a real app, this would check a token in localStorage or cookies
        // and validate it with the backend
        const savedUser = localStorage.getItem('taskfiles_user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
      } catch (error) {
        console.error('Authentication error:', error);
        // Clear any invalid data
        localStorage.removeItem('taskfiles_user');
      } finally {
        // Set loading to false regardless of outcome
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);
  const login = (userData: User) => {
    // In a real app, this would store a token and user info
    setUser(userData);
    localStorage.setItem('taskfiles_user', JSON.stringify(userData));
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('taskfiles_user');
  };
  const switchRole = (role: UserRole) => {
    if (user) {
      const updatedUser = {
        ...user,
        role
      };
      setUser(updatedUser);
      localStorage.setItem('taskfiles_user', JSON.stringify(updatedUser));
    }
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    switchRole
  }} data-id="element-191">
      {children}
    </AuthContext.Provider>;
};
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};